import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Hero component with elegant artistic gradient background and modern typography.
 * Features clean design with compelling messaging and clear call-to-actions.
 */
const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);

  const painPoints = [
    "Using Excel Spreadsheets as Your CRM?",
    "Per-User CRM Fees That Punish You for Growing?",
    "Paying for 100 CRM Features But Only Using 5?",
    "A CRM That Lives in Its Own Island?",
    "Bending Your Business to Fit Your CRM's Limitations?",
    "A CRM So Complex Your Team Avoids Using It?",
    "A CRM That Creates More Work Instead of Saving Time?"
  ];

  // Calculate max height needed for rotating text
  useEffect(() => {
    const calculateHeight = () => {
      const testSpan = document.createElement('span');
      testSpan.style.cssText = `
        position: absolute;
        visibility: hidden;
        display: inline-block;
        padding: 0.5rem 1.5rem;
        font-size: 2.2rem;
        line-height: 1.4;
        max-width: calc(100vw - 4rem);
        word-wrap: break-word;
      `;
      document.body.appendChild(testSpan);

      let maxHeight = 0;
      painPoints.forEach(text => {
        testSpan.textContent = text;
        const height = testSpan.offsetHeight;
        if (height > maxHeight) maxHeight = height;
      });

      document.body.removeChild(testSpan);
      // Add extra buffer for safety (20% more space)
      setContainerHeight(maxHeight * 1.2);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, [painPoints]);

  // Typewriter effect with pause
  useEffect(() => {
    const currentFullText = painPoints[currentIndex];

    if (isPaused) {
      const pauseTimer = setTimeout(() => setIsPaused(false), 2000);
      return () => clearTimeout(pauseTimer);
    }

    const typingSpeed = isDeleting ? 30 : 80;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentFullText.length) {
          setDisplayText(currentFullText.slice(0, displayText.length + 1));
        } else {
          setIsPaused(true);
          setIsDeleting(true);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % painPoints.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, isPaused, currentIndex, painPoints]);

  return (
    <section className="hero" id="hero">
      <div className="hero-content">
        <h1 className="hero-title">
          <em>Are you frustrated with</em>
          <br />
          <span style={{
            display: 'inline-block',
            marginTop: '1rem',
            minHeight: containerHeight ? `${containerHeight}px` : 'auto',
            verticalAlign: 'top'
          }}>
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1.5rem',
              background: 'rgba(255, 255, 255, 0.35)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '12px',
              color: '#000000',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              fontSize: '2.2rem',
              lineHeight: '1.4'
            }}>
              {displayText}
              <span style={{ animation: 'blink 1s step-start infinite' }}>|</span>
            </span>
          </span>
        </h1>
        <p className="hero-subtitle" style={{ fontStyle: 'italic', fontSize: '1.4rem' }}>
          Get a CRM built specifically for your business. Pay once. Own it forever. No monthly fees. No per-user charges. Just a system that actually works for YOU.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginBottom: '2rem' }}>
          <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="btn-primary">Book a Consultation</a>
          <Link to="/services" className="btn-secondary">View Our Services</Link>
        </div>
        <div style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.9)', textAlign: 'center', fontWeight: '500', textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)' }}>
          SuiteCRM specialists • AI automation workflows • Software development
        </div>
      </div>
    </section>
  );
};


export default Hero;